import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faRobot, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

const MCP_PUBLIC_URL = (
  process.env.REACT_APP_MCP_PUBLIC_URL || 'https://mcptrading.mydesktophub.com'
).replace(/\/+$/, '');

/**
 * OAuth consent bridge for the remote trading MCP server.
 * MCP redirects here with ?mcp_session=... after the agent starts OAuth.
 */
const McpOAuthAuthorizePage = () => {
  const [searchParams] = useSearchParams();
  const mcpSession = searchParams.get('mcp_session') || '';
  const { accessToken, user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [preflight, setPreflight] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingPreflight, setLoadingPreflight] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/oauth/authorize?mcp_session=${mcpSession}`)}`, {
        replace: true,
      });
    }
  }, [authLoading, isAuthenticated, mcpSession, navigate]);

  useEffect(() => {
    if (!mcpSession) {
      setError('Missing mcp_session. Start the connection from your MCP client.');
      setLoadingPreflight(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoadingPreflight(true);
        const res = await fetch(
          `${MCP_PUBLIC_URL}/oauth/preflight?mcp_session=${encodeURIComponent(mcpSession)}`,
          { credentials: 'omit' }
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json?.error_description || json?.error || `HTTP ${res.status}`);
        }
        if (!cancelled) setPreflight(json);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load authorization request');
      } finally {
        if (!cancelled) setLoadingPreflight(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mcpSession]);

  const complete = useCallback(
    async (approve) => {
      if (!mcpSession || !accessToken) return;
      setBusy(true);
      setError('');
      try {
        const path = approve ? '/oauth/complete' : '/oauth/deny';
        const body = approve
          ? { mcp_session: mcpSession, jwt: accessToken }
          : { mcp_session: mcpSession };

        const res = await fetch(`${MCP_PUBLIC_URL}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json?.error_description || json?.error || `HTTP ${res.status}`);
        }
        const redirectUrl = json.redirect_url || json.redirectUrl;
        if (!redirectUrl) throw new Error('MCP server did not return redirect_url');
        window.location.href = redirectUrl;
      } catch (err) {
        setError(err.message || 'Authorization failed');
        setBusy(false);
      }
    },
    [accessToken, mcpSession]
  );

  if (authLoading || (!isAuthenticated && mcpSession)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-muted" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted mb-2">LionTrading</div>
          <h1 className="text-[24px] font-semibold tracking-tight text-ink flex items-center gap-2">
            <FontAwesomeIcon icon={faRobot} className="text-base opacity-80" />
            Connect {preflight?.resource_name || 'LionTrading'} to Claude
          </h1>
          <p className="mt-1 text-sm text-muted">
            Claude (or another MCP client) is requesting access to your trading desk and market data.
          </p>
        </div>

        <div className="lt-card p-6 space-y-4">
          {error && (
            <div className="px-3 py-2.5 rounded-md border border-[rgba(240,113,103,0.35)] bg-[var(--lt-bear-dim)] text-bear text-sm">
              {error}
            </div>
          )}

          {loadingPreflight ? (
            <div className="flex items-center gap-2 text-sm text-muted">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              Loading request…
            </div>
          ) : (
            <>
              <div className="text-sm text-ink space-y-2">
                <div>
                  <span className="text-muted">Signed in as</span>{' '}
                  <span className="font-medium">{user?.username || user?.name || 'user'}</span>
                </div>
                {preflight?.client_name && (
                  <div>
                    <span className="text-muted">Client</span>{' '}
                    <span className="font-medium">{preflight.client_name}</span>
                    {preflight.client_origin ? (
                      <span className="text-muted text-xs"> ({preflight.client_origin})</span>
                    ) : null}
                  </div>
                )}
                <div className="text-xs text-muted leading-relaxed pt-1 border-t border-[var(--lt-border)]">
                  Approving lets the agent place and cancel orders, read positions, and pull market
                  data using your LionTrading session. You can disconnect the connector anytime in
                  the MCP client.
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  disabled={busy || !!error}
                  onClick={() => complete(true)}
                  className="flex-1 h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} className="mr-1.5 text-xs" />
                      Approve
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => complete(false)}
                  className="h-10 px-4 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-medium disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1.5 text-xs" />
                  Deny
                </button>
              </div>
            </>
          )}

          <div className="pt-2 text-center">
            <Link to="/" className="text-xs text-muted hover:text-ink">
              Back to desk
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default McpOAuthAuthorizePage;
